import type { Lang } from "../index";

export interface IndependentTechConsultantCallCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  lede: string;
  problem: { heading: string; body1: string; body2: string };
  scenarios: {
    heading: string;
    items: { tag: string; title: string; body: string }[];
  };
  how: {
    heading: string;
    body: string;
    bullets: string[];
  };
  proof: { heading: string; bullets: string[] };
  cta: { heading: string; body: string; primary: string; secondary: string };
}

const en: IndependentTechConsultantCallCopy = {
  seo: {
    title: "Minuta for independent tech consultants - live call copilot",
    description:
      "For senior independent consultants on high-stakes US/EU scoping calls. Minuta suggests architecture, risk, scope, and budget answers live in English, grounded in your own docs - no bot in the call.",
  },
  eyebrow: "Use case · Independent consultant",
  h1: "When the CTO pressure-tests you in English, answer like the senior in the room.",
  lede:
    "The contract is not won by the notes after the call. It is won in the 12 seconds after a CTO asks about architecture risk, scope boundaries, budget, or tradeoffs. Minuta is the live copilot that uses the context you loaded before the call to draft the answer you can say now.",
  problem: {
    heading: "The hard question lands before your English catches up.",
    body1:
      "An international scoping call is not a generic discovery call. The CTO already believes there is a problem. They are deciding whether you can carry the architecture, defend the scope, and explain the tradeoffs when money and delivery risk are on the table.",
    body2:
      "You know the answer from prior engagements, but under live English pressure it comes out smaller: a hedge, a long pause, or an over-explained paragraph. Minuta keeps your case studies, postmortems, proposal notes, rate sheet, and scope template beside the transcript so the next answer is specific before the moment passes.",
  },
  scenarios: {
    heading: "The moments that decide the call",
    items: [
      {
        tag: "Architecture pressure-test",
        title: "\"How would you de-risk this migration?\"",
        body: "Before the call, you loaded the proposal, target architecture notes, and 3 postmortems from similar migrations. When the CTO asks, Minuta suggests: \"I would de-risk it in two phases: shadow traffic for 48 hours, feature flags around the new path, and a rollback lever owned by one person. The main risk is data drift, so I would measure that before cutover.\" The call shifts from abstract confidence to architecture judgment.",
      },
      {
        tag: "Scope boundaries",
        title: "\"What is included, and what is explicitly out of scope?\"",
        body: "You uploaded your scope template, past SOWs, and the client's intake notes. Minuta drafts the boundary in English: \"Included: migration plan, cutover runbook, and two validation checkpoints. Out of scope: rewriting the reporting layer or owning team staffing decisions. If those become necessary, I would price them as a separate workstream.\" You protect margin without sounding defensive.",
      },
      {
        tag: "Budget justification",
        title: "\"Your rate is high - why should we pay it?\"",
        body: "You loaded your rate sheet, testimonials, and outcome notes from prior projects. Minuta surfaces the receipts: \"The rate maps to risk reduction: in similar migrations I shortened the decision loop, avoided one failed cutover, and left the team with a repeatable runbook. If useful, I can walk through the economics against the cost of a delayed migration.\" The conversation moves from price to value.",
      },
      {
        tag: "Seniority proof",
        title: "\"What tradeoff would you challenge us on?\"",
        body: "You attached discovery notes and your architecture tradeoff checklist. Minuta suggests a pointed but diplomatic answer: \"I would challenge doing platform cleanup and product delivery in the same milestone. It looks efficient, but it hides two different risks. I would separate the dependency cleanup from the customer-visible release and make the acceptance criteria explicit.\" You sound like someone who has seen the failure mode before.",
      },
    ],
  },
  how: {
    heading: "How Minuta works during the scoping call",
    body:
      "Open Minuta before the Zoom, Teams, or Meet call. Attach the material you would normally keep in a private prep doc: proposal notes, architecture diagrams, case studies, postmortems, rate sheet, scope template, and the client's intake context. Then run the call normally.",
    bullets: [
      "No bot joins the meeting. The CTO sees only you; Minuta runs on your computer and captures system audio.",
      "Live English transcript stays beside the call, so fast follow-ups, accents, and stacked questions do not become memory tests.",
      "When a risk, architecture, scope, or budget question lands, ask Minuta what to say. It drafts a 2-4 second answer in your tone, grounded in the docs you loaded before the call.",
      "After the call, you still get the structured summary: what they asked for, what you committed to, budget range discussed, risks raised, and follow-up actions.",
    ],
  },
  proof: {
    heading: "Why this is not another notetaker",
    bullets: [
      "Generic notetakers help after the meeting. Minuta helps during the part where the CTO is deciding whether you are the senior operator they can trust.",
      "Otter, Tactiq, and Fireflies summarize what was said. They do not turn your own postmortems, scope boundaries, and rate rationale into a live English answer.",
      "A bot in the call can weaken rapport on a sensitive commercial conversation. Minuta stays invisible.",
      "Zero-retention processing keeps architecture details, pricing rationale, and client context on your computer.",
    ],
  },
  cta: {
    heading: "Try it before your next scoping call",
    body: "7 days with everything unlocked. No credit card. €9/mo after.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: IndependentTechConsultantCallCopy = {
  seo: {
    title: "Minuta para consultor independente - copiloto ao vivo",
    description:
      "Para consultores independentes seniores em scoping calls de alto risco com US/EU. O Minuta sugere respostas de arquitetura, risco, escopo e orçamento ao vivo em inglês, com base nos seus docs - sem bot na call.",
  },
  eyebrow: "Caso de uso · Consultor independente",
  h1: "Quando o CTO te pressure-testa em inglês, responda como o sênior da sala.",
  lede:
    "O contrato não é ganho pela ata depois da call. Ele é ganho nos 12 segundos depois que o CTO pergunta sobre risco de arquitetura, limites de escopo, orçamento ou tradeoffs. O Minuta é o copiloto ao vivo que usa o contexto que você carregou antes da call para montar a resposta que você pode dizer agora.",
  problem: {
    heading: "A pergunta difícil chega antes do seu inglês.",
    body1:
      "Uma scoping call internacional não é uma discovery genérica. O CTO já acredita que existe um problema. Ele está decidindo se você consegue carregar a arquitetura, defender o escopo e explicar os tradeoffs quando dinheiro e risco de entrega estão na mesa.",
    body2:
      "Você sabe a resposta por causa dos engagements anteriores, mas sob pressão ao vivo em inglês ela sai menor: um hedge, uma pausa longa, ou um parágrafo explicado demais. O Minuta mantém seus case studies, postmortems, notas de proposta, rate sheet e template de escopo ao lado do transcript para a próxima resposta sair específica antes do momento passar.",
  },
  scenarios: {
    heading: "Os momentos que decidem a call",
    items: [
      {
        tag: "Pressure-test de arquitetura",
        title: "\"How would you de-risk this migration?\"",
        body: "Antes da call, você carregou a proposta, notas da arquitetura alvo e 3 postmortems de migrações parecidas. Quando o CTO pergunta, o Minuta sugere: \"I would de-risk it in two phases: shadow traffic for 48 hours, feature flags around the new path, and a rollback lever owned by one person. The main risk is data drift, so I would measure that before cutover.\" A call sai de confiança abstrata para julgamento de arquitetura.",
      },
      {
        tag: "Limites de escopo",
        title: "\"What is included, and what is explicitly out of scope?\"",
        body: "Você subiu seu template de escopo, SOWs anteriores e as notas de intake do cliente. O Minuta monta o limite em inglês: \"Included: migration plan, cutover runbook, and two validation checkpoints. Out of scope: rewriting the reporting layer or owning team staffing decisions. If those become necessary, I would price them as a separate workstream.\" Você protege margem sem soar defensivo.",
      },
      {
        tag: "Justificativa de orçamento",
        title: "\"Your rate is high - why should we pay it?\"",
        body: "Você carregou o rate sheet, depoimentos e notas de outcome de projetos anteriores. O Minuta puxa os receipts: \"The rate maps to risk reduction: in similar migrations I shortened the decision loop, avoided one failed cutover, and left the team with a repeatable runbook. If useful, I can walk through the economics against the cost of a delayed migration.\" A conversa muda de preço para valor.",
      },
      {
        tag: "Prova de senioridade",
        title: "\"What tradeoff would you challenge us on?\"",
        body: "Você anexou notas de discovery e seu checklist de tradeoffs de arquitetura. O Minuta sugere uma resposta direta e diplomática: \"I would challenge doing platform cleanup and product delivery in the same milestone. It looks efficient, but it hides two different risks. I would separate the dependency cleanup from the customer-visible release and make the acceptance criteria explicit.\" Você soa como alguém que já viu aquele modo de falha.",
      },
    ],
  },
  how: {
    heading: "Como o Minuta funciona durante a scoping call",
    body:
      "Abra o Minuta antes da call no Zoom, Teams ou Meet. Anexe o material que você normalmente manteria num prep doc privado: notas de proposta, diagramas de arquitetura, case studies, postmortems, rate sheet, template de escopo e contexto de intake do cliente. Depois rode a call normalmente.",
    bullets: [
      "Nenhum bot entra na reunião. O CTO vê só você; o Minuta roda no seu computador e captura o áudio do sistema.",
      "O transcript em inglês ao vivo fica ao lado da call, então follow-ups rápidos, sotaques e perguntas empilhadas não viram teste de memória.",
      "Quando cai uma pergunta de risco, arquitetura, escopo ou orçamento, peça ao Minuta o que dizer. Ele monta em 2-4 segundos uma resposta no seu tom, fundamentada nos docs que você carregou antes da call.",
      "Depois da call, você ainda recebe a ata estruturada: o que pediram, com o que você se comprometeu, faixa de orçamento discutida, riscos levantados e próximos passos.",
    ],
  },
  proof: {
    heading: "Por que isso não é mais um notetaker",
    bullets: [
      "Notetakers genéricos ajudam depois da reunião. O Minuta ajuda durante a parte em que o CTO decide se você é o operador sênior em quem dá para confiar.",
      "Otter, Tactiq e Fireflies resumem o que foi dito. Eles não transformam seus postmortems, limites de escopo e racional de rate em uma resposta ao vivo em inglês.",
      "Um bot na call pode enfraquecer o rapport numa conversa comercial sensível. O Minuta fica invisível.",
      "Processamento com retenção zero mantém detalhes de arquitetura, racional de preço e contexto do cliente no seu computador.",
    ],
  },
  cta: {
    heading: "Teste antes da sua próxima scoping call",
    body: "7 dias com tudo desbloqueado. Sem cartão. €9/mês depois.",
    primary: "Começar trial grátis",
    secondary: "Baixar para macOS",
  },
};

const es: IndependentTechConsultantCallCopy = {
  seo: {
    title: "Minuta para consultores independientes - copiloto en vivo",
    description:
      "Para consultores independientes sénior en scoping calls de alto riesgo con US/EU. Minuta sugiere respuestas de arquitectura, riesgo, scope y presupuesto en vivo en inglés, basadas en tus docs - sin bot en la call.",
  },
  eyebrow: "Caso de uso · Consultor independiente",
  h1: "Cuando el CTO te pressure-testea en inglés, responde como el sénior de la sala.",
  lede:
    "El contrato no se gana con las notas después de la call. Se gana en los 12 segundos después de que el CTO pregunta sobre riesgo de arquitectura, límites de scope, presupuesto o tradeoffs. Minuta es el copiloto en vivo que usa el contexto que cargaste antes de la call para armar la respuesta que puedes decir ahora.",
  problem: {
    heading: "La pregunta difícil llega antes que tu inglés.",
    body1:
      "Una scoping call internacional no es una discovery genérica. El CTO ya cree que hay un problema. Está decidiendo si puedes cargar con la arquitectura, defender el scope y explicar los tradeoffs cuando el dinero y el riesgo de entrega están sobre la mesa.",
    body2:
      "Sabes la respuesta por engagements anteriores, pero bajo presión en vivo en inglés sale más pequeña: un hedge, una pausa larga, o un párrafo demasiado explicado. Minuta mantiene tus case studies, postmortems, notas de propuesta, rate sheet y template de scope al lado del transcript para que la próxima respuesta salga específica antes de que pase el momento.",
  },
  scenarios: {
    heading: "Los momentos que deciden la call",
    items: [
      {
        tag: "Pressure-test de arquitectura",
        title: "\"How would you de-risk this migration?\"",
        body: "Antes de la call, cargaste la propuesta, notas de arquitectura objetivo y 3 postmortems de migraciones parecidas. Cuando el CTO pregunta, Minuta sugiere: \"I would de-risk it in two phases: shadow traffic for 48 hours, feature flags around the new path, and a rollback lever owned by one person. The main risk is data drift, so I would measure that before cutover.\" La call pasa de confianza abstracta a criterio de arquitectura.",
      },
      {
        tag: "Límites de scope",
        title: "\"What is included, and what is explicitly out of scope?\"",
        body: "Subiste tu template de scope, SOWs anteriores y las notas de intake del cliente. Minuta arma el límite en inglés: \"Included: migration plan, cutover runbook, and two validation checkpoints. Out of scope: rewriting the reporting layer or owning team staffing decisions. If those become necessary, I would price them as a separate workstream.\" Proteges margen sin sonar defensivo.",
      },
      {
        tag: "Justificación de presupuesto",
        title: "\"Your rate is high - why should we pay it?\"",
        body: "Cargaste tu rate sheet, testimonios y notas de outcome de proyectos anteriores. Minuta saca los receipts: \"The rate maps to risk reduction: in similar migrations I shortened the decision loop, avoided one failed cutover, and left the team with a repeatable runbook. If useful, I can walk through the economics against the cost of a delayed migration.\" La conversación se mueve de precio a valor.",
      },
      {
        tag: "Prueba de seniority",
        title: "\"What tradeoff would you challenge us on?\"",
        body: "Adjuntaste notas de discovery y tu checklist de tradeoffs de arquitectura. Minuta sugiere una respuesta directa y diplomática: \"I would challenge doing platform cleanup and product delivery in the same milestone. It looks efficient, but it hides two different risks. I would separate the dependency cleanup from the customer-visible release and make the acceptance criteria explicit.\" Suenas como alguien que ya vio ese modo de fallo.",
      },
    ],
  },
  how: {
    heading: "Cómo funciona Minuta durante la scoping call",
    body:
      "Abre Minuta antes de la call en Zoom, Teams o Meet. Adjunta el material que normalmente tendrías en un prep doc privado: notas de propuesta, diagramas de arquitectura, case studies, postmortems, rate sheet, template de scope y contexto de intake del cliente. Luego corre la call normalmente.",
    bullets: [
      "Ningún bot entra a la reunión. El CTO solo te ve a ti; Minuta corre en tu computadora y captura el audio del sistema.",
      "El transcript en inglés en vivo queda al lado de la call, así que follow-ups rápidos, acentos y preguntas apiladas no se vuelven una prueba de memoria.",
      "Cuando cae una pregunta de riesgo, arquitectura, scope o presupuesto, pide a Minuta qué decir. Arma en 2-4 segundos una respuesta en tu tono, basada en los docs que cargaste antes de la call.",
      "Después de la call, todavía recibes el acta estructurada: qué pidieron, a qué te comprometiste, rango de presupuesto discutido, riesgos levantados y próximos pasos.",
    ],
  },
  proof: {
    heading: "Por qué esto no es otro notetaker",
    bullets: [
      "Los notetakers genéricos ayudan después de la reunión. Minuta ayuda durante la parte en la que el CTO decide si eres el operador sénior en quien puede confiar.",
      "Otter, Tactiq y Fireflies resumen lo que se dijo. No convierten tus postmortems, límites de scope y racional de rate en una respuesta en vivo en inglés.",
      "Un bot en la call puede debilitar el rapport en una conversación comercial sensible. Minuta queda invisible.",
      "El procesamiento con retención cero mantiene los detalles de arquitectura, el racional de precio y el contexto del cliente en tu computadora.",
    ],
  },
  cta: {
    heading: "Pruébalo antes de tu próxima scoping call",
    body: "7 días con todo desbloqueado. Sin tarjeta. €9/mes después.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const independentTechConsultantCallCopy: Record<Lang, IndependentTechConsultantCallCopy> = { en, pt, es };
